<div class="invite-team-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title class="dialog-title">Manage Team</h2>
    <button
      mat-icon-button
      class="close-button"
      (click)="onCancel()"
      [disabled]="isLoading()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div mat-dialog-content class="dialog-content">
    <div class="invite-section">
      <div class="email-input-container">
        <mat-form-field class="email-input-field" appearance="outline">
          <mat-label>Email, comma separated</mat-label>
          <mat-chip-grid #chipGrid aria-label="Enter emails">
            @for (email of emails(); track $index) {
              <mat-chip-row
                (removed)="remove(email)"
                [editable]="false"
                class="email-chip"
                [class.error-chip]="isExistingMemberEmail(email)"
              >
                {{ email }}
                <button matChipRemove [attr.aria-label]="'remove ' + email">
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip-row>
            }
            <input
              #emailInput
              [formControl]="emailCtrl"
              [matChipInputFor]="chipGrid"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [matChipInputAddOnBlur]="false"
              (matChipInputTokenEnd)="add($event)"
              (keydown)="onKeyDown($event)"
              [matAutocomplete]="auto"
            />
          </mat-chip-grid>
          <div matSuffix class="role-badge-inline"
            [class.role-badge-hidden]="emails().length === 0"
            [ngClass]="selectedRoleClass()"
            [matMenuTriggerFor]="roleSelectorMenu"
            [matTooltip]="'Add as ' + selectedRoleName()"
            matTooltipClass="figma-role-tooltip"
          >
            <span class="role-icon-inline" [ngClass]="selectedRoleClass()">
              <img [src]="getRoleIconPath(selectedRoleId())" [alt]="selectedRoleName()" />
            </span>
            <span class="role-label-inline">{{ selectedRoleName() }}</span>
            <mat-icon class="role-dropdown-icon">arrow_drop_down</mat-icon>
          </div>
          <mat-menu #roleSelectorMenu="matMenu">
            @for (role of roles(); track role.roleId) {
              @let operatorUnavailable = isOperatorRoleOption(role) && !hasPublishedVersions();
              <button
                mat-menu-item
                (click)="onRoleOptionActivated($event, role.roleId!, operatorUnavailable)"
                [appAriaDisabled]="operatorUnavailable"
                [disableRipple]="operatorUnavailable"
                [class.role-menu-item-unavailable]="operatorUnavailable"
                [matTooltip]="noPublishedVersionsMessage"
                [matTooltipDisabled]="!operatorUnavailable"
                matTooltipClass="figma-role-tooltip"
              >
                <mat-icon class="menu-role-icon" [svgIcon]="getRoleSvgIcon(role.roleId!)"></mat-icon>
                <span>{{ getRoleName(role.roleId!) }}</span>
              </button>
            }
          </mat-menu>
          <mat-autocomplete
            #auto="matAutocomplete"
            class="invite-team-autocomplete-panel"
            (optionSelected)="selected($event)"
            [displayWith]="displayUserFn"
          >
            @for (user of filteredUsers | async; track user.userId) {
              <mat-option [value]="user" class="py-2">
                <div class="flex items-center">
                  @if (user.name) {
                    <div class="user-avatar w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">
                      {{ user.name.substring(0, 2).toUpperCase() }}
                    </div>
                  }
                  <div class="overflow-hidden">
                    @if (user.name) {
                      <div class="font-medium text-sm text-gray-800 truncate">{{ user.name }}</div>
                    }
                    <div class="text-xs text-gray-500 truncate">{{ user.email }}</div>
                  </div>
                </div>
              </mat-option>
            }
          </mat-autocomplete>
          @if (showEmailError()) {
            <mat-error>Please enter valid email addresses</mat-error>
          }
        </mat-form-field>

        <button
          mat-raised-button
          color="primary"
          class="invite-button"
          [disabled]="emails().length === 0 || isLoading()"
          (click)="onInvite()"
        >
          @if (isLoading()) {
            <mat-icon class="spinner">refresh</mat-icon>
          }
          @if (!inviteProgress().isProcessing) {
            <span>Invite</span>
          }
          @if (inviteProgress().isProcessing) {
            <span>
              Inviting {{ inviteProgress().current }}/{{ inviteProgress().total }}
            </span>
          }
        </button>
      </div>
    </div>

    <div class="members-section">
      <div class="members-section-header">
        <span class="members-count">Project Members ({{ teamMembers().length }})</span>
        <button class="sort-button" (click)="toggleSort()">
          <span>Sort</span>
          <mat-icon>swap_vert</mat-icon>
        </button>
      </div>

      @if (isLoadingTeam()) {
        <div class="loading-indicator">
          <mat-icon class="spinner">refresh</mat-icon>
          <span>Loading team members...</span>
        </div>
      }

      @if (!isLoadingTeam() && teamMembers().length === 0) {
        <div class="empty-state">
          <mat-icon>group</mat-icon>
          <p>No team members found</p>
          <span>Invite team members using the email field above</span>
        </div>
      }

      <div class="team-member-list">
        @for (member of teamMembers(); track member.id) {
          <div class="member-row">
            <div
              class="mat-avatar"
              [ngClass]="getMemberAvatarClass(member)"
            >
              {{ member.initials }}
            </div>

            <div class="user-info">
              <div class="user-name-row">
                <span class="user-name">{{ member.name }}</span>
                <span
                  class="role-icon-badge"
                  [matTooltip]="getRoleName(member.roleId, member.isOwner)"
                >
                  <img
                    [src]="getRoleIconPath(member.roleId, member.isOwner)"
                    [alt]="getRoleName(member.roleId, member.isOwner)"
                  />
                </span>
                @if (member.isPending) {
                  <mat-icon class="pending-icon" matTooltip="Pending invite">schedule</mat-icon>
                }
              </div>
              <div class="user-email">{{ member.email }}</div>
            </div>

            @if (!member.isOwner && canManageTeam()) {
              <button
                mat-icon-button
                class="member-menu-btn"
                [matMenuTriggerFor]="memberMenu"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #memberMenu="matMenu" xPosition="before">
                @if (!member.isPending) {
                  @for (role of roles(); track role.roleId) {
                    @if (role.roleId !== member.roleId && role.roleId !== 0) {
                      @let operatorUnavailable = isOperatorRoleOption(role) && !hasPublishedVersions();
                      <button
                        mat-menu-item
                        (click)="onMemberRoleOptionActivated($event, member, role.roleId!, operatorUnavailable)"
                        [appAriaDisabled]="operatorUnavailable"
                        [disableRipple]="operatorUnavailable"
                        [class.role-menu-item-unavailable]="operatorUnavailable"
                        [matTooltip]="noPublishedVersionsMessage"
                        [matTooltipDisabled]="!operatorUnavailable"
                        matTooltipClass="figma-role-tooltip"
                      >
                        <mat-icon class="menu-role-icon" [svgIcon]="getRoleSvgIcon(role.roleId!)"></mat-icon>
                        <span>{{ getMakeRoleLabel(role.roleId!) }}</span>
                      </button>
                    }
                  }
                }
                <button mat-menu-item class="remove-option" (click)="removeMember(member)">
                  <mat-icon class="menu-role-icon remove-icon">cancel</mat-icon>
                  <span>{{ member.isPending ? 'Cancel invite' : 'Remove' }}</span>
                </button>
              </mat-menu>
            }

          </div>
        }
      </div>
    </div>
  </div>
</div>
